import { ForkEffect, all, call, put, select, takeEvery } from 'redux-saga/effects';
import {
  FAVOURITES_KEY,
  IFavouritesState,
  IStoredFavourites,
  MAX_TOP_FAVOURITES,
  createFavouriteApi,
} from 'redux-state/favourites/types.js';
import { FavouriteLocationSummary } from 'types/markerTypes/location.favourites.js';
import { isDevEnv } from 'utilities/consts/webpack-consts.js';
import { makeRequest } from 'utilities/fetch/api-gateway.js';
import { getLocalStorageItem, setLocalStorageItem } from 'utilities/helpers/local-storage.js';
import { isNotNullOrUndefined } from 'utilities/isType.js';
import {
  REDUX_FAVOURITES_ADD_ASYNC,
  REDUX_FAVOURITES_ADD_ERROR,
  REDUX_FAVOURITES_ADD_SUCCESS,
  REDUX_FAVOURITES_LOAD_ASYNC,
  REDUX_FAVOURITES_LOAD_RESPONSE,
} from './reducer.js';

/** Fetch the favourites data from the faviorute urls  */
function* fetchFavouritesData() {
  const favourites: IStoredFavourites = getLocalStorageItem(FAVOURITES_KEY) || { items: [], topCount: 0 };
  if (favourites.items.length === 0) {
    yield put(REDUX_FAVOURITES_LOAD_RESPONSE({ items: [], topCount: 0 }));
  } else {
    try {
      const allFavouriteRequests: FavouriteLocationSummary[] = yield all(
        favourites.items
          .filter((favUrlSuffix) => favUrlSuffix && favUrlSuffix.length)
          .map(async (favUrlSuffix) => {
            try {
              const location = await makeRequest<FavouriteLocationSummary>({
                path: `/favourites/${favUrlSuffix}`,
                options: { credentials: 'include' },
                apiPrefix: true,
              });
              return location;
            } catch (e) {
              if (isDevEnv(false)) {
                console.warn(e);
              }
              return false;
            }
          })
          .filter(Boolean),
      );

      const filteredFavouriteItems = favourites.items
        .map((favUrlSuffix: string) => {
          const favs = allFavouriteRequests.find((val: FavouriteLocationSummary) => {
            const result = createFavouriteApi(val) === favUrlSuffix;
            return result;
          });
          return favs;
        })
        .filter(isNotNullOrUndefined);

      yield put(REDUX_FAVOURITES_LOAD_RESPONSE({ items: filteredFavouriteItems, topCount: favourites.topCount }));
    } catch {
      yield put(REDUX_FAVOURITES_LOAD_RESPONSE({ items: [], topCount: 0 }));
    }
  }
}

function* addFavourite(action: ReturnType<typeof REDUX_FAVOURITES_ADD_ASYNC>) {
  const { key } = action.payload;

  // Check if a favouritesList already exists
  const stateFaves: IFavouritesState = yield select((state) => state.FAVOURITES);

  const favouritesList = stateFaves.topItems.concat(stateFaves.bottomItems);

  // Strip out just the keys from the favourites list
  const favouritesPathArr = favouritesList.map((item) => createFavouriteApi(item));

  /** does favourite already exist in favourites list */
  if (key && favouritesPathArr.indexOf(key) === -1) {
    try {
      const favRequest: FavouriteLocationSummary = yield call(makeRequest, {
        path: `/favourites/${key}`,
        options: { credentials: 'include' },
        apiPrefix: true,
      });

      // If there's no filteredFavouriteItem and not empty object (this happens!!) then return
      if (favRequest && Object.keys(favRequest).length) {
        const addToTopList = stateFaves.topItems.length < MAX_TOP_FAVOURITES;
        const newFavList = [...favouritesPathArr, key];
        const addedToLocalStorage = setLocalStorageItem(FAVOURITES_KEY, {
          items: [...favouritesPathArr, key],
          topCount: addToTopList ? newFavList.length : MAX_TOP_FAVOURITES,
        });
        if (addedToLocalStorage) {
          yield put(REDUX_FAVOURITES_ADD_SUCCESS({ item: favRequest, toTop: addToTopList }));
        }
      }
    } catch (e) {
      console.log('Failed to retrieve favourite for ', key, e);
      yield put(REDUX_FAVOURITES_ADD_ERROR({ key }));
    }
  } else {
    console.log(`A favourite item already exists with the id ${key}`);
  }
}

export default function* favouritesSaga(): IterableIterator<ForkEffect<never>> {
  const loadType = REDUX_FAVOURITES_LOAD_ASYNC().type;
  yield takeEvery(loadType, fetchFavouritesData);
  const addType = REDUX_FAVOURITES_ADD_ASYNC({ key: 'test' }).type;
  yield takeEvery(addType, addFavourite);
}
